import styled from "@emotion/styled";
import React from "react";
import { UseFormSetValue } from "react-hook-form";
import { match } from "ts-pattern";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { DOCUMENT_OUTLINE_ICON, REFRESH_ICON } from "../../../utils/svgs_new";
import { AppButton } from "../../Generic/AppButton";
import { AppIcon } from "../../Generic/AppIcon";
import Flex from "../../Generic/Flex";
import { If } from "../../Generic/If";
import { isDefined } from "../../Generic/IsDefined";
import { getFileType } from "../../Onboarding/utils/onboardingUtils";
import PdfViewer from "../../PdfViewer";
import UploadPhoto from "../../UploadPhoto";
import UploadPlaceholder from "../../UploadPlaceholder";
import { OnboardingForm } from "../hooks/useOnboardingForm";

interface Props {
  initialData?: string;
  fieldName: string;
  isDisabled: boolean;
  setValue: UseFormSetValue<OnboardingForm>;
  canUploadFromGallery?: boolean;
  isSelfie?: boolean;
  onClickDisabled: () => void;
}

const UploadPhotoWrapper = styled.div``;

const UploadPhotosWrapper = styled.div`
  ${UploadPhotoWrapper} + ${UploadPhotoWrapper} {
    margin-top: 8px;
  }
`;

const AddDocumentButton = styled(AppButton)`
  width: 100%;
  margin: 8px 0;
`;

function QuestionDocumentMultiple(props: Props) {
  const [documents, setDocuments] = React.useState<(string | null)[]>(() => {
    const urls = props.initialData?.split("|");

    if (urls === undefined || urls.length === 0) {
      return [null];
    }

    return [...urls];
  });

  const handleAddDocument = () => {
    setDocuments([...documents, null]);
  };

  const setDocument = (index: number, value: string | null) => {
    const newDocuments = [...documents.slice(0, index), value, ...documents.slice(index + 1)];

    setDocuments(newDocuments);

    props.setValue(props.fieldName, newDocuments.filter(isDefined).join("|"));
  };

  const canAddAnotherDocument =
    documents.length > 0 && !documents.some((document) => document === null) && !props.isDisabled;

  return (
    <UploadPhotosWrapper>
      {documents.map((photo, i) => (
        <UploadPhotoWrapper key={i}>
          <UploadPhoto
            photo={photo ?? undefined}
            isDisabled={props.isDisabled}
            canUploadFromGallery={props.canUploadFromGallery}
            isSelfie={props.isSelfie}
            onSelect={(base64) => setDocument(i, base64 ?? null)}
            onClickDisabled={props.onClickDisabled}
          >
            {($props) =>
              match($props)
                .with({ type: "Idle" }, QuestionDocumentMultiple.IdleResult)
                .with({ type: "Processing" }, QuestionDocumentMultiple.ProcessingResult)
                .with({ type: "Error" }, QuestionDocumentMultiple.ErrorResult)
                .with({ type: "Success" }, QuestionDocumentMultiple.SuccessResult)
                .exhaustive()
            }
          </UploadPhoto>
        </UploadPhotoWrapper>
      ))}
      <If condition={canAddAnotherDocument}>
        <AddDocumentButton onClick={handleAddDocument} variant={"outline"} type="button">
          + {translate(STRINGS.ADD_ANOTHER_DOCUMENT)}
        </AddDocumentButton>
      </If>
    </UploadPhotosWrapper>
  );
}

QuestionDocumentMultiple.IdleResult = (props: { isSelfie?: boolean; open: () => void }) => {
  return <UploadPlaceholder isSelfie={props.isSelfie} onClick={props.open} />;
};

QuestionDocumentMultiple.ProcessingResult = () => {
  return <div> ${translate(STRINGS.PROCESSING)}...</div>;
};

QuestionDocumentMultiple.ErrorResult = (props: { error: string }) => {
  return (
    <div>
      ${STRINGS.ERROR}: {props.error}
    </div>
  );
};

QuestionDocumentMultiple.SuccessResult = (props: {
  url: string;
  isSelfie?: boolean;
  open: () => void;
  isDisabled: boolean;
}) => {
  return (
    <Flex yGap={8} direction="column">
      <DisplayDocument url={props.url} />
      {props.isDisabled ? null : (
        <AppButton type="button" variant="link" size="small" color="black" onClick={props.open}>
          <AppIcon icon={REFRESH_ICON} size={16} />
          <span>
            {translate(
              props.isSelfie === true ? STRINGS.TAKE_A_DIFFERENT_PHOTO : STRINGS.CHANGE_DOCUMENT
            )}
          </span>
        </AppButton>
      )}
    </Flex>
  );
};

function DisplayDocument(props: { url: string }) {
  const type = getFileType(props.url);

  return match(type)
    .with("image", () => <img width="100%" src={props.url} />)
    .with("pdf", () => {
      if (props.url.startsWith("https://")) {
        return <PdfViewer url={props.url}></PdfViewer>;
      }

      return (
        <DisplayDocument.AttachmentLabel>
          <AppIcon icon={DOCUMENT_OUTLINE_ICON} size={24} />
          <span>{translate(STRINGS.ONE_DOCUMENT_SELECTED)}</span>
        </DisplayDocument.AttachmentLabel>
      );
    })
    .exhaustive();
}

DisplayDocument.AttachmentLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  font-weight: 500;
  border-radius: 8px;
  padding: 8px;
`;

export default QuestionDocumentMultiple;
